import api from './api';

export interface DartCloudTime {
  hour?: number;
  minute?: number;
}

export interface OpeningHour {
  start?: DartCloudTime;
  end?: DartCloudTime;
}

export interface ClubAddress {
  country?: string | null;
  label?: string | null;
  city?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  postcode?: string | null;
  street?: string | null;
  query?: string | null;
}

export interface TeamLink {
  id?: string;
  name?: string;
  url?: string;
  icon?: string | null;
}

export interface ClubTeam {
  id?: string;
  name?: string;
  description?: string | null;
  league?: string | null;
  image?: string | null;
  images?: string[];
  links?: TeamLink[];
  tableLink?: string | null;
  socialMediaCollection?: any;
}

export interface SocialMediaCollection {
  facebook?: string | null;
  instagram?: string | null;
  tiktok?: string | null;
  twitter?: string | null;
  youtube?: string | null;
  twitch?: string | null;
  website?: string | null;
}

export interface ClubTrainingSchedule {
  monday?: OpeningHour[];
  tuesday?: OpeningHour[];
  wednesday?: OpeningHour[];
  thursday?: OpeningHour[];
  friday?: OpeningHour[];
  saturday?: OpeningHour[];
  sunday?: OpeningHour[];
}

export interface ClubCommunityImage {
  image?: string;
  contributorId?: string;
  contributorName?: string;
  addedAt?: number;
}

export interface Club {
  _id?: string;
  id?: string;
  name?: string;
  slug?: string;
  description?: string | null;
  image?: string | null;
  images?: string[] | null;
  communityImages?: ClubCommunityImage[];
  logo?: string | null;

  address?: ClubAddress;

  email?: string | null;
  phone?: string | null;

  membershipFile?: string | null;
  membershipInformation?: string | null;
  membershipLink?: string | null;

  steeldart?: boolean | null;
  edart?: boolean | null;

  openTraining?: ClubTrainingSchedule | null;
  closedTraining?: ClubTrainingSchedule | null;

  teams?: ClubTeam[];
  associations?: string[];
  leagues?: string[];
  pubs?: string[];
  tournaments?: string[];
  series?: string[];

  socialMediaCollection?: SocialMediaCollection | null;
  tags?: string[] | null;

  owner?: string;
  admins?: string[];
  createdBy?: string | null;
  createdAt?: number;
  updatedAt?: number | null;
  validUntil?: number;
  visible?: boolean;
  sponsored?: boolean;
  views?: number;
}

export interface ClubDetailResponse {
  success: boolean;
  data: Club | null;
}

/**
 * Normalisiert Detail-Responses defensiv. Das Backend liefert die Entität
 * DIREKT (res.json(club)); ältere/andere Endpunkte evtl. als {success, data}.
 * Beides wird unterstützt → garantiert {success, data}.
 */
export function normalizeDetail<T>(body: any): { success: boolean; data: T | null } {
  if (!body || typeof body !== 'object') return { success: false, data: null };
  const looksLikeEntity =
    '_id' in body || 'id' in body || 'slug' in body || 'name' in body;
  const entity = looksLikeEntity ? body : 'data' in body ? body.data : body;
  return { success: !!entity, data: (entity ?? null) as T | null };
}

class ClubService {
  async getClubBySlug(slug: string): Promise<ClubDetailResponse> {
    const res = await api.get(`/api/club/slug/${encodeURIComponent(slug)}`);
    return normalizeDetail<Club>(res.data);
  }

  async getClubById(id: string): Promise<ClubDetailResponse> {
    const res = await api.get(`/api/club/${encodeURIComponent(id)}`);
    return normalizeDetail<Club>(res.data);
  }

  async createClub(payload: any): Promise<any> {
    const res = await api.post('/api/club/create', payload);
    return res.data;
  }

  async updateClub(payload: any): Promise<any> {
    const res = await api.post('/api/club/update', payload);
    return res.data;
  }

  async deleteClub(id: string): Promise<any> {
    const res = await api.post('/api/club/delete', { id });
    return res.data;
  }

  async addImage(id: string, data: string): Promise<any> {
    const res = await api.post('/api/clubs/image/add', { id, data });
    return res.data;
  }

  async removeImage(id: string, image: string): Promise<any> {
    const res = await api.post('/api/clubs/image/remove', { id, image });
    return res.data;
  }

  buildFileUrl(fileId?: string | null): string | null {
    if (!fileId) return null;
    if (fileId.startsWith('http://') || fileId.startsWith('https://')) return fileId;
    const base = api.defaults.baseURL || '';
    return `${base}/api/file/${fileId}`;
  }

  formatAddress(address?: ClubAddress | null): string {
    if (!address) return 'Adresse nicht hinterlegt';
    if (address.label && address.label.trim().length > 0) return address.label;
    const parts: string[] = [];
    if (address.street) parts.push(address.street);
    const cityLine = [address.postcode, address.city].filter(Boolean).join(' ');
    if (cityLine) parts.push(cityLine);
    if (address.country) parts.push(address.country);
    return parts.length ? parts.join(', ') : 'Adresse nicht hinterlegt';
  }

  hasAnyTrainingTimes(schedule?: ClubTrainingSchedule | null): boolean {
    if (!schedule) return false;
    const days: (keyof ClubTrainingSchedule)[] = [
      'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday',
    ];
    return days.some((d) => Array.isArray(schedule[d]) && (schedule[d] as OpeningHour[]).length > 0);
  }
}

export default new ClubService();
